/*eslint max-lines-per-function: ["error", 170]*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import { TabsHistory } from '../../../app/components/TabsHistory';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import momencik from '../../../common/momencik';
import { useNavigation } from '../../../common/navigation';
import { GetInvoicePaymentDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import { InvoicePaymentBasicData } from '../../components/InvoicePayment/InvoicePaymentBasicData';
import { InvoicePaymentLinks } from '../../components/InvoicePayment/InvoicePaymentLinks';
import numberFormatter from '../../../common/numberFormatter';
import { useLocation } from '../../../common/hooks/useLocation';

export enum InvoicePaymentEditorTabs {
  BASIC_DATA = 'basicData',
  LINKS = 'links',
}

interface TabProps {
  key: InvoicePaymentEditorTabs;
  title: string;
  component: React.ReactNode;
  disabled: boolean;
  notSaved?: boolean;
}

const getTabClassName = (notSaved?: boolean) =>
  notSaved ? 'not-saved-tab' : '';

const getTabTitle = (title: string, notSaved?: boolean) =>
  notSaved ? `${title} (NIEZAPISANE)` : title;

type ParamTypes = {
  invoicePaymentId: string;
};

export const InvoicePaymentEditorView: React.FC = () => {
  const { invoicePaymentId: invoicePaymentIdFromParams } =
    useParams<ParamTypes>();
  const spd = useSpdCore();
  const nav = useNavigation();
  const navigate = useNavigate();
  const location = useLocation<{ tabKey?: string }>();

  const [notSavedTab, setNotSavedTab] = useState<
    InvoicePaymentEditorTabs | undefined
  >(undefined);
  const [remaining, setRemaining] = useState<number | undefined>(undefined);
  const [invoicePaymentId, setInvoicePaymentId] = useState<string | undefined>(
    invoicePaymentIdFromParams,
  );
  const [invoicePayment, setInvoicePayment] = useState<
    GetInvoicePaymentDto | undefined
  >(undefined);
  const [activeTab, setActiveTab] = useState<string>(
    InvoicePaymentEditorTabs.BASIC_DATA,
  );
  const paymentTitleText = invoicePayment?.title || '';

  useEffect(() => {
    setInvoicePaymentId(invoicePaymentIdFromParams);
    setInvoicePayment(undefined);
  }, [invoicePaymentIdFromParams]);

  const loadInvoicePayment = useCallback(() => {
    if (!invoicePaymentId) {
      return;
    }
    spd.invoicePayments
      .getById(invoicePaymentId)
      .then(data => setInvoicePayment(data))
      .catch(console.error);
  }, [invoicePaymentId, spd.invoicePayments]);

  useEffect(() => {
    if (invoicePaymentId) {
      loadInvoicePayment();
    }
  }, [invoicePaymentId, loadInvoicePayment]);

  const navigateToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      void navigate(location.pathname + (location.search || ''), {
        state: { tabKey: tab },
        replace: true,
      });
    },
    [navigate, location.pathname, location.search],
  );

  const tabs: TabProps[] = useMemo(
    () => [
      {
        key: InvoicePaymentEditorTabs.BASIC_DATA,
        title: 'Dane wpłaty',
        component: (
          <InvoicePaymentBasicData
            invoicePayment={invoicePayment}
            invoicePaymentId={invoicePaymentId}
            setInvoicePaymentId={setInvoicePaymentId}
            setNotSavedTab={setNotSavedTab}
            setActiveTab={navigateToTab}
            refreshInvoicePayment={async () => loadInvoicePayment()}
          />
        ),
        disabled: false,
        notSaved: notSavedTab === InvoicePaymentEditorTabs.BASIC_DATA,
      },
      {
        key: InvoicePaymentEditorTabs.LINKS,
        title: 'Powiązania z dokumentami',
        component: (
          <InvoicePaymentLinks
            invoicePaymentId={invoicePaymentId}
            invoicePayment={invoicePayment}
            paymentAmount={invoicePayment?.amount}
            onRemainingChange={setRemaining}
            setNotSavedTab={setNotSavedTab}
          />
        ),
        disabled: !invoicePaymentId,
        notSaved: notSavedTab === InvoicePaymentEditorTabs.LINKS,
      },
    ],
    [
      invoicePayment,
      invoicePaymentId,
      notSavedTab,
      loadInvoicePayment,
      navigateToTab,
    ],
  );

  const title = invoicePayment
    ? `Wpłata z dnia ${momencik(invoicePayment.paymentDate)} 
    na kwotę ${numberFormatter(invoicePayment.amount, true) ?? ''} 
    (nr sys. ${invoicePayment.systemNumber})`
    : 'Rejestracja nowej wpłaty';

  const senderText = invoicePayment?.sender || '';
  const remainingText =
    remaining !== undefined
      ? `Pozostało do dowiązania: ${numberFormatter(remaining ?? 0, true) || '0,00 zł'}`
      : '';
  const isRemainingWarning = remaining !== undefined && remaining !== 0;

  return (
    <>
      <Card className="main-card">
        <Card.Body className="m-3">
          <Card.Title className="pb-3">
            <div>{title}</div>
            {(senderText || paymentTitleText) && (
              <div className="fs-6 fw-normal text-muted mt-1 d-flex flex-column gap-1">
                {senderText && <div>Nadawca: {senderText}</div>}
                {paymentTitleText && <div>Tytuł: {paymentTitleText}</div>}
              </div>
            )}
            {remainingText && (
              <div
                className={
                  isRemainingWarning
                    ? 'fs-6 fw-bold text-danger mt-1 gap-1'
                    : 'fs-6 fw-normal text-muted mt-1 gap-1'
                }
              >
                {remainingText}
              </div>
            )}
          </Card.Title>
          <TabsHistory
            activeKey={activeTab}
            setActiveTab={setActiveTab}
            id="invoice-payment-editor"
            className="my-3"
          >
            {tabs.map(({ key, title, component, notSaved, disabled }) => (
              <Tab
                key={key}
                eventKey={key}
                tabClassName={getTabClassName(notSaved)}
                title={getTabTitle(title, notSaved)}
                disabled={disabled}
                className="m-3"
              >
                {component}
              </Tab>
            ))}
          </TabsHistory>
        </Card.Body>
      </Card>
      <div className="mt-3 ms-3">
        <Button
          variant="outline-secondary"
          onClick={() => nav.invoicePayments()}
        >
          Przejdź do listy wpłat
        </Button>
      </div>
    </>
  );
};
